import { Injectable, OnDestroy, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import {
  GetSRJData,
  ResetStatus,
  ResetSteps,
  UpdateSRJCompletedSteps,
  UpdateSRJDuration,
  UpdateSRJGoal,
  UpdateSRJHelpfulValue,
  UpdateSRJMnemonicPhrases,
  UpdateSRJSelectedDimension,
  UpdateSRJSelectedTopic,
  UpdateSRJSelectedValues,
  UpdateSRJTimeSlot,
  UpdateSRJUnhelpfulValue,
} from './selfReflectionJourney.actions';
import { ISrjCompletedStep, ISrjCompletedStepBEModel } from '../../interfaces/ISrjCompeltedStep';
import { SRJ_TOPIC } from '../../enums/srj_topic.enum';
import { SRJ_DIMENSIONS } from '../../enums/srj_dimensions.enum';
import { SRJ_VALUES } from '../../enums/srj_values.enum';
import { SelfReflectionJourneyService } from '../../services/self-reflection-journey.service';
import { tap } from 'rxjs/operators';
import { ISelfReflectionJourneyUserData } from '../../interfaces/ISelfReflectionJourneyUserData';
import { SRJ_DURATION } from '../../enums/srj_duration.enum';
import { PersistedStatus, STATUS } from '../../types/self-reflection-journey-status.type';
import { SRJOperations } from '../../../self-reflection-journeys/self-reflection-journey/srj.operations';

export class SelfReflectionJourneyStateModel {
  completedSteps: ISrjCompletedStep[];
  status: PersistedStatus | null; //if last step is completed => finished
  selectedTopic: SRJ_TOPIC | string; //from step stage1_3-2-1 or stage1_3-1-1
  selectedDimension: SRJ_DIMENSIONS; // from topic
  goal: string; //from step stage1_5-2-7 or stage1_5-1
  values: SRJ_VALUES[]; //from step stage2_7
  helpfulValue: SRJ_VALUES;
  unhelpfulValue: SRJ_VALUES;
  mnemonicPhrases: string[];
  duration: SRJ_DURATION | string; // from step stage4_5
  timeSlot: string; // from step stage4_6
}

@State<SelfReflectionJourneyStateModel>({
  name: 'selfReflectionJourney',
  defaults: {
    status: null,
    completedSteps: [],
    selectedTopic: null,
    selectedDimension: null,
    goal: null,
    values: [],
    helpfulValue: null,
    unhelpfulValue: null,
    mnemonicPhrases: [],
    duration: null,
    timeSlot: null,
  },
})
@Injectable()
export class SelfReflectionJourneyState implements OnDestroy {
  private srjService = inject(SelfReflectionJourneyService);

  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  @Selector()
  static selfReflectionJourneyStatus(state: SelfReflectionJourneyStateModel): PersistedStatus | null {
    return state.status;
  }

  @Selector()
  static srjSelectedTopic(state: SelfReflectionJourneyStateModel): SRJ_TOPIC | string {
    return state.selectedTopic;
  }

  @Selector()
  static srjSelectedDimension(state: SelfReflectionJourneyStateModel): SRJ_DIMENSIONS {
    return state.selectedDimension;
  }

  @Selector()
  static srjSelectedValues(state: SelfReflectionJourneyStateModel): SRJ_VALUES[] {
    return state.values;
  }

  @Selector()
  static selfReflectionJourneyCompletedSteps(state: SelfReflectionJourneyStateModel): ISrjCompletedStep[] {
    return state.completedSteps;
  }

  @Selector()
  static selfReflectionJourneyGoal(state: SelfReflectionJourneyStateModel): string {
    return state.goal;
  }

  @Selector()
  static selfReflectionJourneyHelpfulValue(state: SelfReflectionJourneyStateModel): SRJ_VALUES {
    return state.helpfulValue;
  }

  @Selector()
  static selfReflectionJourneyUnhelpfulValue(state: SelfReflectionJourneyStateModel): SRJ_VALUES {
    return state.unhelpfulValue;
  }

  @Selector()
  static selfReflectionJourneyMnemonicPhrases(state: SelfReflectionJourneyStateModel): string[] {
    return state.mnemonicPhrases;
  }

  @Selector()
  static selfReflectionJourneyDuration(state: SelfReflectionJourneyStateModel): SRJ_DURATION | string {
    return state.duration;
  }

  @Selector()
  static selfReflectionJourneyTimeSlot(state: SelfReflectionJourneyStateModel): string {
    return state.timeSlot;
  }

  @Action(GetSRJData)
  GetSRJData(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { userGid: string }): Observable<ISelfReflectionJourneyUserData> {
    return this.srjService.getSRJUserData().pipe(
      tap(userData => {
        if (!userData) {
          this.srjService.saveSelfReflectionRecord().subscribe();
          ctx.patchState({
            status: null,
          });
        } else {
          if (userData.steps && userData.steps.length > 0) {
            ctx.patchState({
              completedSteps: userData.steps.map(step => SRJOperations.mapStepDataFromBE(step)),
              mnemonicPhrases: [],
            });
            this.srjService.updateStateData(userData.steps);
            ctx.patchState({
              status: STATUS.STARTED, // to do update completed
            });
          }
        }
      }),
    );
  }

  @Action(ResetSteps)
  ResetSteps(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { newStepsArray: ISrjCompletedStep[] }): Observable<ISelfReflectionJourneyUserData> {
    const mappedPayload = payload.newStepsArray.map(step => SRJOperations.mapStepDataFromFE(step));
    return this.srjService.saveSelfReflectionRecord(mappedPayload).pipe(
      tap(userData => {
        ctx.patchState({
          completedSteps: userData.steps.map(step => SRJOperations.mapStepDataFromBE(step)),
          selectedTopic: null,
          selectedDimension: null,
          goal: null,
          values: [],
          helpfulValue: null,
          unhelpfulValue: null,
          mnemonicPhrases: [],
        });
        this.srjService.updateStateData(userData.steps);
      }),
    );
  }

  @Action(UpdateSRJCompletedSteps)
  UpdateSRJCompletedSteps(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { newStep: ISrjCompletedStep }): Observable<ISrjCompletedStepBEModel> {
    return this.srjService.updateSRJCompletedSteps(payload.newStep).pipe(
      tap(newStep => {
        const newCompletedSteps = [...ctx.getState().completedSteps, SRJOperations.mapStepDataFromBE(newStep)];
        ctx.patchState({
          completedSteps: newCompletedSteps,
        });
      }),
    );
  }

  @Action(UpdateSRJSelectedTopic)
  UpdateSRJSelectedTopic(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { topic: SRJ_TOPIC | string }): void {
    ctx.patchState({
      selectedTopic: payload.topic,
    });
  }

  @Action(UpdateSRJSelectedDimension)
  UpdateSRJSelectedDimension(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { dimension: SRJ_DIMENSIONS }): void {
    ctx.patchState({
      selectedDimension: payload.dimension,
    });
  }

  @Action(UpdateSRJMnemonicPhrases)
  UpdateSRJMnemonicPhrases(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { phrases: string[] }): void {
    const phrases = ctx.getState().mnemonicPhrases;
    ctx.patchState({
      mnemonicPhrases: [...phrases, ...payload.phrases],
    });
  }

  @Action(UpdateSRJGoal)
  UpdateSRJGoal(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { goal: string }): void {
    ctx.patchState({
      goal: payload.goal,
    });
  }

  @Action(UpdateSRJSelectedValues)
  UpdateSRJSelectedValues(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { values: SRJ_VALUES[] }): void {
    ctx.patchState({
      values: payload.values,
    });
  }

  @Action(UpdateSRJHelpfulValue)
  UpdateSRJHelpfulValue(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { value: SRJ_VALUES }): void {
    ctx.patchState({
      helpfulValue: payload.value,
    });
  }

  @Action(UpdateSRJDuration)
  UpdateSRJDuration(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { duration: SRJ_DURATION | string }): void {
    ctx.patchState({
      duration: payload.duration,
    });
  }

  @Action(UpdateSRJTimeSlot)
  UpdateSRJTimeSlot(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { timeSlot: SRJ_DURATION | string }): void {
    ctx.patchState({
      timeSlot: payload.timeSlot,
    });
  }

  @Action(UpdateSRJUnhelpfulValue)
  UpdateSRJUnhelpfulValue(ctx: StateContext<SelfReflectionJourneyStateModel>, payload: { value: SRJ_VALUES }): void {
    ctx.patchState({
      unhelpfulValue: payload.value,
    });
  }

  @Action(ResetStatus)
  ResetStatus(ctx: StateContext<SelfReflectionJourneyStateModel>): Observable<any> {
    return this.srjService.resetStatus().pipe(
      tap(() => {
        this.srjService.saveSelfReflectionRecord().subscribe();
        ctx.setState({
          status: null,
          completedSteps: [],
          selectedTopic: null,
          selectedDimension: null,
          goal: null,
          values: [],
          helpfulValue: null,
          unhelpfulValue: null,
          mnemonicPhrases: [],
          duration: null,
          timeSlot: null,
        });
      }),
    );
  }
}
